<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useOwner } from "../../stores/website/owner";

export type Color = "primary" | "bronze" | "silver" | "gold" | "white";
export type Size = "1" | "2" | "3" | "4" | "5" | "6";
export type Font = "sans-serif" | "monospace" | "primary" | "secondary" | "code";

export interface Style {
    color: Color;
    size: Size;
}

export interface PackPriceProps {
    price?: number;
    color?: Color;
    size?: Size;
    font?: Font;
    precision?: number;
    textColor?: Color;
    textSize?: Size;
    textFont?: Font;
    priceColor?: Color;
    priceSize?: Size;
    priceFont?: Font;
    isTaxLess?: boolean;
    sign: "+" | "-" | "";
}

const { owner } = storeToRefs(useOwner());

const props = withDefaults(defineProps<PackPriceProps>(), {
    price: 0,
    color: "white",
    size: "5",
    font: "sans-serif",
    isTaxLess: false,
    sign: "",
    precision: 0,
});

const textColor = props.textColor ?? props.color;
const textSize = props.textSize ?? props.size;
const textFont = props.textFont ?? props.font;
const priceColor = props.priceColor ?? props.color;
const priceSize = props.priceSize ?? props.size;
const priceFont = props.priceFont ?? props.font;
</script>

<template>
    <div>
        <p
            v-if="props.price !== 0"
            class="inherit-weight aligned-text"
            :class="[
                `has-text-${textColor}`,
                `is-size-${textSize}`,
                `text-height-${textSize}`,
                `is-family-${textFont}`,
            ]">
            <span
                :class="[
                    `has-text-${priceColor}`,
                    `is-size-${priceSize}`,
                    `text-height-${priceSize}`,
                    `is-family-${priceFont}`,
                ]">
                {{ props.sign }}
                <PriceView
                    :price="props.price"
                    :precision="props.precision"
                    :owner="owner"
                    :with-currency="true"></PriceView>
            </span>
            <br v-if="textSize !== priceSize" />
            <Translate v-if="!props.isTaxLess" path="speed2max.packCard.price" />
            <Translate v-else path="speed2max.packCard.priceHT" />
        </p>
        <p
            v-else
            :class="[
                `has-text-${textColor}`,
                `is-size-${textSize}`,
                `text-height-${textSize}`,
                `is-family-${textFont}`,
            ]"
            class="inherit-weight">
            <Translate path="speed2max.packCard.noPrice" />
        </p>
    </div>
</template>

<style lang="scss" scoped>
.aligned-text {
    text-align: center;
}

.inherit-weight {
    font-weight: inherit;
}

.text-height-1 {
    line-height: 3rem;
}

.text-height-2 {
    line-height: 2.5rem;
}

.text-height-3 {
    line-height: 2rem;
}

.text-height-4 {
    line-height: 1.5rem;
}

.text-height-5 {
    line-height: 1.25rem;
}

.text-height-6 {
    line-height: 1rem;
}
</style>
